// backend/live_web/src/components/SessionsTable.tsx
/**
 * The session list's columns, printed with the eval dashboard's `DataTable`: the header a
 * tinted strip, one hairline between rows, the whole row clickable, and the first cell a real
 * link for the keyboard and "copy link address".
 *
 * `DataTable` is `table-fixed`: the pill and date columns state pixel widths wide enough for
 * their content, and the working diagnosis — the one prose column — takes the remainder.
 */

import type { ReactNode } from "react";

import type { AdminSessionListItem } from "../api/types";
import { calendarDate, clockTime, EM_DASH } from "../lib/format";
import { patientDetails } from "../lib/patient";
import { URGENCY_PENDING_TITLE } from "../lib/urgency";
import Acuity from "./ui/Acuity";
import DataTable, { type Column } from "./ui/DataTable";
import StatusTag from "./ui/StatusTag";

function patientDisplayName(item: AdminSessionListItem): string {
  return item.patient_name?.trim() || item.user.name || "No name";
}

/**
 * Six columns on a wide window. Below `lg` the email column is dropped and the address moves
 * under the patient's name, so the table still fits instead of scrolling its working diagnosis
 * out of view.
 */
const COLUMNS: readonly Column<AdminSessionListItem>[] = [
  {
    id: "patient",
    header: "Patient",
    width: "w-[200px]",
    cell: (item) => {
      const details = patientDetails(item.patient_age, item.patient_sex, ", ");
      return (
        <span className="block min-w-0">
          <span className="block text-ink">{patientDisplayName(item)}</span>
          {details !== "" ? (
            <span className="block text-caption font-normal text-ink-faint">{details}</span>
          ) : null}
          <span
            className="block truncate text-caption font-normal text-ink-faint lg:hidden"
            title={item.user.email}
          >
            {item.user.email}
          </span>
        </span>
      );
    },
  },
  {
    id: "email",
    header: "Email",
    width: "w-[220px]",
    className: "hidden lg:table-cell",
    cell: (item) => (
      <span className="block truncate font-normal text-ink-3" title={item.user.email}>
        {item.user.email}
      </span>
    ),
  },
  {
    id: "urgency",
    header: "Urgency",
    width: "w-[132px]",
    cell: (item) => <Acuity urgency={item.urgency_level} missingTitle={URGENCY_PENDING_TITLE} />,
  },
  {
    id: "diagnosis",
    header: "Working diagnosis",
    // The one prose column: it takes the remainder, and a long diagnosis is held to two lines
    // with the whole of it on hover, so one session cannot make its row six lines tall.
    cell: (item) =>
      item.working_diagnosis ? (
        <span className="line-clamp-2 text-ink" title={item.working_diagnosis}>
          {item.working_diagnosis}
        </span>
      ) : (
        <span className="text-ink-faint">{EM_DASH}</span>
      ),
  },
  {
    id: "status",
    header: "Status",
    width: "w-[132px]",
    cell: (item) => <StatusTag status={item.status} />,
  },
  {
    id: "created",
    header: "Created",
    width: "w-[120px]",
    cell: (item) => (
      <span className="block whitespace-nowrap">
        <span className="block text-ink">{calendarDate(item.created_at)}</span>
        <span className="block text-caption font-normal text-ink-faint">
          {clockTime(item.created_at)}
        </span>
      </span>
    ),
  },
];

type Props = {
  items: readonly AdminSessionListItem[];
  /** Shown in place of the rows when there are none. */
  empty: ReactNode;
};

export default function SessionsTable({ items, empty }: Props) {
  return (
    <DataTable
      caption="Patient voice sessions"
      columns={COLUMNS}
      rows={items}
      keyOf={(item) => item.id}
      hrefOf={(item) => `/sessions/${item.id}`}
      empty={empty}
      minWidth="min-w-[720px] lg:min-w-[1000px]"
    />
  );
}
